/**
 * Merge multiple project PDFs and migrate comments, markup, measurements, and take-offs.
 */

import { PDFDocument } from 'pdf-lib';
import {
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  type DocumentData,
  type DocumentReference,
  type UpdateData,
} from 'firebase/firestore';
import type { ProjectFile } from '../types';
import { db } from './firebase';
import { uploadProjectFile } from './projectFileService';

const BATCH_LIMIT = 450;

const OVERLAY_COLLECTIONS = [
  'pdfAnnotations',
  'documentMarkup',
  'pdfMeasurements',
  'pdfMeasurementCalibrations',
  'pdfTakeoffPins',
  'pdfTakeoffLinks',
] as const;

export interface PdfMergeSourceFile {
  id: string;
  name: string;
  url: string;
}

export interface PdfMergeProgress {
  phase: 'fetching' | 'merging' | 'uploading' | 'migrating' | 'done';
  message: string;
  step?: number;
  totalSteps?: number;
}

export interface MergeProjectPdfsInput {
  projectId: string;
  sources: PdfMergeSourceFile[];
  outputName: string;
  uploadedBy: string;
  uploadedByName: string;
  dealId?: string;
  onProgress?: (progress: PdfMergeProgress) => void;
}

export interface MergeProjectPdfsResult {
  mergedFile: ProjectFile;
  pageCounts: number[];
  migratedCounts: Record<string, number>;
}

async function fetchPdfBytes(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to download ${url} (${res.status})`);
  return res.arrayBuffer();
}

async function mergePdfBuffers(buffers: ArrayBuffer[]): Promise<{ bytes: Uint8Array; pageCounts: number[] }> {
  const merged = await PDFDocument.create();
  const pageCounts: number[] = [];
  for (const buffer of buffers) {
    const src = await PDFDocument.load(buffer);
    const count = src.getPageCount();
    pageCounts.push(count);
    const copied = await merged.copyPages(src, src.getPageIndices());
    for (const page of copied) merged.addPage(page);
  }
  const bytes = await merged.save();
  return { bytes, pageCounts };
}

interface OverlayDocRef {
  ref: DocumentReference;
  pageNumber: number;
}

async function queryOverlayDocs(
  collectionName: string,
  documentId: string,
  documentUrl: string,
): Promise<OverlayDocRef[]> {
  const byId = await getDocs(query(collection(db, collectionName), where('documentId', '==', documentId)));
  const seen = new Set<string>();
  const results: OverlayDocRef[] = [];

  const addSnap = (snap: { id: string; ref: OverlayDocRef['ref']; data: () => DocumentData }) => {
    if (seen.has(snap.id)) return;
    seen.add(snap.id);
    const data = snap.data();
    const pageNumber = typeof data.pageNumber === 'number' ? data.pageNumber : 1;
    results.push({ ref: snap.ref, pageNumber });
  };

  for (const snap of byId.docs) addSnap(snap);

  if (documentUrl && documentUrl !== documentId) {
    const byUrl = await getDocs(query(collection(db, collectionName), where('documentUrl', '==', documentUrl)));
    for (const snap of byUrl.docs) addSnap(snap);
  }

  return results;
}

async function batchUpdateOverlayRefs(
  refs: OverlayDocRef[],
  buildUpdate: (pageNumber: number) => UpdateData<DocumentData>,
): Promise<number> {
  if (refs.length === 0) return 0;
  let batch = writeBatch(db);
  let ops = 0;
  let count = 0;
  for (const { ref, pageNumber } of refs) {
    batch.update(ref, buildUpdate(pageNumber));
    ops += 1;
    count += 1;
    if (ops >= BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
  }
  if (ops > 0) await batch.commit();
  return count;
}

async function migrateOverlaysForSource(options: {
  source: PdfMergeSourceFile;
  pageOffset: number;
  newDocumentId: string;
  newDocumentUrl: string;
}): Promise<Record<string, number>> {
  const { source, pageOffset, newDocumentId, newDocumentUrl } = options;
  const documentId = source.id || source.url;
  const counts: Record<string, number> = {};

  for (const collectionName of OVERLAY_COLLECTIONS) {
    const refs = await queryOverlayDocs(collectionName, documentId, source.url);
    counts[collectionName] = await batchUpdateOverlayRefs(refs, oldPage => ({
      documentId: newDocumentId,
      documentUrl: newDocumentUrl,
      pageNumber: oldPage + pageOffset,
    }));
  }

  return counts;
}

export async function mergeProjectPdfs(input: MergeProjectPdfsInput): Promise<MergeProjectPdfsResult> {
  const { projectId, sources, outputName, uploadedBy, uploadedByName, dealId, onProgress } = input;
  if (sources.length < 2) throw new Error('Select at least two PDFs to merge.');

  onProgress?.({ phase: 'fetching', message: 'Downloading PDFs…', step: 0, totalSteps: sources.length });
  const buffers: ArrayBuffer[] = [];
  for (let i = 0; i < sources.length; i += 1) {
    onProgress?.({
      phase: 'fetching',
      message: `Downloading ${sources[i].name}…`,
      step: i + 1,
      totalSteps: sources.length,
    });
    buffers.push(await fetchPdfBytes(sources[i].url));
  }

  onProgress?.({ phase: 'merging', message: 'Merging pages…' });
  const { bytes, pageCounts } = await mergePdfBuffers(buffers);

  onProgress?.({ phase: 'uploading', message: 'Uploading merged PDF…' });
  const safeName = outputName.trim() || `Merged-${Date.now()}.pdf`;
  const fileName = safeName.toLowerCase().endsWith('.pdf') ? safeName : `${safeName}.pdf`;
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const mergedFile = await uploadProjectFile(
    projectId,
    new File([blob], fileName, { type: 'application/pdf' }),
    uploadedBy,
    uploadedByName,
    dealId ? { dealId } : undefined,
  );

  if (!mergedFile.id) throw new Error('Upload succeeded but merged file has no id.');

  const newDocumentId = mergedFile.id;
  const newDocumentUrl = mergedFile.url;
  const migratedCounts: Record<string, number> = {};
  let pageOffset = 0;

  for (let i = 0; i < sources.length; i += 1) {
    onProgress?.({
      phase: 'migrating',
      message: `Moving annotations from ${sources[i].name}…`,
      step: i + 1,
      totalSteps: sources.length,
    });
    const perCollection = await migrateOverlaysForSource({
      source: sources[i],
      pageOffset,
      newDocumentId,
      newDocumentUrl,
    });
    for (const [key, val] of Object.entries(perCollection)) {
      migratedCounts[key] = (migratedCounts[key] ?? 0) + val;
    }
    pageOffset += pageCounts[i];
  }

  onProgress?.({ phase: 'done', message: 'Merge complete.' });
  return { mergedFile, pageCounts, migratedCounts };
}

export function defaultMergedPdfName(sources: PdfMergeSourceFile[]): string {
  if (sources.length === 0) return 'Merged.pdf';
  if (sources.length === 1) return `${sources[0].name.replace(/\.pdf$/i, '')}-copy.pdf`;
  const base = sources[0].name.replace(/\.pdf$/i, '');
  return `${base} + ${sources.length - 1} more.pdf`;
}

export function summarizeMigrationCounts(counts: Record<string, number>): string {
  const labels: Record<string, string> = {
    pdfAnnotations: 'comments',
    documentMarkup: 'markup strokes',
    pdfMeasurements: 'measurements',
    pdfMeasurementCalibrations: 'calibrations',
    pdfTakeoffPins: 'take-off pins',
    pdfTakeoffLinks: 'take-off items',
  };
  const parts = Object.entries(counts)
    .filter(([, n]) => n > 0)
    .map(([key, n]) => `${n} ${labels[key] ?? key}`);
  return parts.length > 0 ? parts.join(', ') : 'no overlays to migrate';
}
